import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Archive, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

export default function Categorie() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("entrata");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: categories = [] } = useQuery({
    queryKey: ["admin-categories-full"],
    queryFn: async () => (await api.get("/administration/categories", { params: { include_archived: true } })).data,
  });

  const filtered = useMemo(() => categories.filter((c) => c.kind === tab), [categories, tab]);

  const archiveMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/administration/categories/${id}`)).data,
    onSuccess: () => { toast.success("Categoria aggiornata"); qc.invalidateQueries({ queryKey: ["admin-categories-full"] }); qc.invalidateQueries({ queryKey: ["admin-categories"] }); },
  });

  return (
    <div className="p-6 max-w-[1100px] mx-auto space-y-5" data-testid="categorie-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#1C352D]">Categorie</h1>
          <p className="text-sm text-stone-500 mt-1">Voci di bilancio per organizzare entrate e uscite.</p>
        </div>
        <Button onClick={() => { setEditing({ name: "", kind: tab, color: tab === "entrata" ? "#16a34a" : "#dc2626" }); setOpen(true); }} data-testid="new-cat-btn" className="bg-[#1C352D] hover:bg-[#264A3E]"><Plus size={14} className="mr-1"/> Nuova categoria</Button>
      </div>

      <div className="flex gap-2 border-b border-stone-200">
        <button onClick={() => setTab("entrata")} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "entrata" ? "border-emerald-600 text-emerald-800" : "border-transparent text-stone-500 hover:text-stone-700"}`} data-testid="cat-tab-in">
          <TrendingUp size={14} className="inline mr-1"/> Entrate
        </button>
        <button onClick={() => setTab("uscita")} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "uscita" ? "border-red-600 text-red-800" : "border-transparent text-stone-500 hover:text-stone-700"}`} data-testid="cat-tab-out">
          <TrendingDown size={14} className="inline mr-1"/> Uscite
        </button>
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">Nessuna categoria</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {filtered.map((c) => (
              <li key={c.id} className={`p-4 flex items-center gap-3 ${c.archived ? "opacity-50" : ""}`} data-testid={`cat-row-${c.id}`}>
                <span className="w-3 h-8 rounded-full shrink-0" style={{ background: c.color }}/>
                <div className="flex-1">
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="flex gap-1 mt-1">
                    {c.is_preset && <Badge className="bg-stone-100 text-stone-600 text-[9px] rounded-full border border-stone-200">preset</Badge>}
                    {c.archived && <Badge className="bg-amber-100 text-amber-800 text-[9px] rounded-full border border-amber-200">archiviata</Badge>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-cat-${c.id}`}><Pencil size={12} className="mr-1"/> Modifica</Button>
                {!c.archived && <Button size="sm" variant="ghost" className="text-stone-500 hover:text-red-700" onClick={() => archiveMut.mutate(c.id)}><Archive size={12} className="mr-1"/> Archivia</Button>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {open && <CategoryDialog category={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => { qc.invalidateQueries({ queryKey: ["admin-categories-full"] }); qc.invalidateQueries({ queryKey: ["admin-categories"] }); }}/>}
    </div>
  );
}

function CategoryDialog({ category, onClose, onSaved }) {
  const [form, setForm] = useState(category);
  const [saving, setSaving] = useState(false);
  const isEdit = !!category?.id;

  const save = async () => {
    if (!form.name) { toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, kind: form.kind, color: form.color };
      if (isEdit) await api.patch(`/administration/categories/${category.id}`, payload);
      else await api.post(`/administration/categories`, payload);
      toast.success(isEdit ? "Aggiornata" : "Creata"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{isEdit ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="cat-name"/></div>
          <div>
            <Label>Tipo</Label>
            <div className="flex gap-2 mt-1">
              <button type="button" onClick={() => setForm({ ...form, kind: "entrata" })} className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium border ${form.kind === "entrata" ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-stone-500 border-stone-200"}`}>Entrata</button>
              <button type="button" onClick={() => setForm({ ...form, kind: "uscita" })} className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium border ${form.kind === "uscita" ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-stone-500 border-stone-200"}`}>Uscita</button>
            </div>
          </div>
          <div><Label>Colore</Label><Input type="color" value={form.color || "#1C352D"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1 h-10"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="cat-save" className="bg-[#1C352D] hover:bg-[#264A3E]">{saving ? "…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
